import React, { useEffect, useState } from "react";
import { Users, Award } from "lucide-react";
import HowItWorks from "../components/HowItWorks";
import "./About.css";

const About = () => {
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setContentVisible(true), 150);
  }, []);

  const roles = [
    "Directors",
    "Writers",
    "Editors",
    "Cinematographers",
    "Producers",
    "Actors",
    "Composers",
    "Executive Producers",
  ];

  return (
    <div className={`about-page ${contentVisible ? "content-visible" : ""}`}>
      {/* Hero — full width, outside constrained wrapper */}
      <section className="about-hero">
        <div className="about-hero-text">
          <p className="about-hero-eyebrow">ABOUT SHORT CUTS NYC</p>
          <h1 className="about-hero-title">
            Democratize
            <br />
            <em>Filmmaking.</em>
          </h1>
          <p className="about-hero-subtitle">
            Short Cuts NYC is a filmmaking collective that breaks down the
            barriers standing between people and the films they want to make. We
            believe cost, connections, and experience should never stop anyone
            from telling their story. So we remove them. Every month, we give
            you the rules, the collaborators, the deadline, and a live audience
            to screen your work. No budget required. No experience necessary.
            Just show up and make something.
          </p>
        </div>
        <div
          className="about-hero-image"
          style={{ backgroundImage: "url(/screening.png)" }}
        >
          <img src="/screening.png" alt="Short Cuts NYC community screening" />
        </div>
      </section>
      {/* Mission */}
      <section
        className="photo-section photo-section-right"
        style={{ "--photo-url": "url(/about2.jpg)" }}
      >
        <div className="photo-section-image" />
        <div className="photo-section-text">
          <p className="photo-section-eyebrow">OUR MISSION</p>
          <h2 className="photo-section-title">
            Making Filmmaking
            <br />
            <em>Accessible.</em>
          </h2>
          <p className="photo-section-body">
            Short Cuts exists to make filmmaking accessible to{" "}
            <em>anyone, regardless of income or resources.</em> We do that by
            providing a space for filmmakers to make films and screen them, meet
            collaborators, and follow a structure and deadline that takes them
            through a complete pipeline from production to public screening
            every single month. No time to be too precious.{" "}
            <em>Grab your camera, and make a film.</em>
          </p>
        </div>
      </section>
      {/* Stats */}
      <section
        className="stats-photo-section"
        style={{ "--photo-url": "url(/about3.png)" }}
      >
        <div className="stats-photo-overlay" />
        <div className="stats-photo-content">
          <p className="stats-eyebrow">BY THE NUMBERS</p>
          <div className="stats-grid">
            <div className="stat-block">
              <div className="stat-block-number">
                <span>500</span>+
              </div>
              <div className="stat-block-label">Films Screened</div>
            </div>
            <div className="stat-block">
              <div className="stat-block-number">
                <span>40</span>+
              </div>
              <div className="stat-block-label">Screenings Held</div>
            </div>
            <div className="stat-block">
              <div className="stat-block-number">
                <span>30</span>+
              </div>
              <div className="stat-block-label">First Time Filmmakers</div>
            </div>
            <div className="stat-block">
              <div className="stat-block-number">
                <span>3</span>+
              </div>
              <div className="stat-block-label">Years Running</div>
            </div>
          </div>
        </div>
      </section>
      {/* Constrained content below photo sections */}
      <div className="about-content">
        {/* How It Works — with blurred photo background */}
        <HowItWorks backgroundImage="/about4.png" />

        {/* Community */}
        <section className="community-section">
          <div className="community-inner">
            <div className="community-text">
              <h2 className="section-heading">
                <Users size={26} />
                The Community Is the Resource
              </h2>
              <p>
                Short Cuts' power lies in its community. You get to screen the
                film you made, but you also get to connect with other
                filmmakers, actors, and artists across the city. That network is
                what makes Short Cuts more than a challenge series. It's a
                living creative hub where relationships are built film by film,
                screening by screening.
              </p>
              <p>
                Whether you've never touched a camera or you're a seasoned
                director looking for creative constraints, Short Cuts gives you
                the collaborators, the deadline, and the stage.
              </p>
            </div>

            <div className="discord-callout">
              <img
                src="/discord.svg"
                alt="Discord"
                className="discord-callout-icon"
              />
              <div className="discord-callout-text">
                <strong>Find your crew on Discord</strong>
                <span>
                  Our Discord is where filmmakers connect between screenings.
                  Find cast, crew, and collaborators for your next Short Cuts
                  project.
                </span>
              </div>
              <a
                href="https://discord.gg/b9KRsFd2bQ"
                target="_blank"
                rel="noopener noreferrer"
                className="discord-callout-button"
              >
                Join the Server
              </a>
            </div>
          </div>
        </section>

        {/* NYFA */}
        <section className="nyfa-section">
          <div className="nyfa-logo-placeholder">
            <img src="/nyfa-logo.png" alt="New York Foundation for the Arts" />
          </div>
          <div className="nyfa-text">
            <h2 className="section-heading">
              <Award size={26} />
              Fiscal Sponsorship
            </h2>
            <p>
              Short Cuts NYC is a sponsored project by the{" "}
              <a
                href="https://www.nyfa.org/projects/project-info/?id=CA2043"
                target="_blank"
                rel="noopener noreferrer"
              >
                New York Foundation for the Arts (NYFA)
              </a>
              . This sponsorship enables us to receive tax-deductible donations
              and grants in support of our mission to democratize filmmaking in
              New York City.
            </p>
          </div>
          <a
            href="https://www.nyfa.org/projects/project-info/?id=CA2043"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-button-primary"
          >
            Donate
          </a>
        </section>

        {/* Contact / CTA */}
        <section className="contact-section">
          <h2>Get Involved</h2>
          <p>
            Whether you're interested in partnering with us, hosting a
            screening, supporting our mission, or simply joining the community —
            we'd love to hear from you.
          </p>
          <div className="contact-buttons">
            <a
              href="mailto:shortcutsny@gmail.com"
              className="contact-button-primary"
            >
              Contact Us
            </a>
            <a
              href="https://www.meetup.com/short-cuts/"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-button-secondary"
            >
              Join on Meetup
            </a>
          </div>
        </section>
      </div>{" "}
      {/* end about-content */}
    </div>
  );
};

export default About;
